import type { NodeKind, SplitKind } from '@/core/document'
import type { ExportFormat, ResizeMode } from '@/core/export'
import type { OutputPresetId } from '@/content/instagram'
import type { ContactChannel } from '@/content/contact'
import type { ToolTab } from '@/core/store/uiState'
import type { UtilityId } from '@/tools/types'

export enum ReportingStage {
  Application = 'application',
  ExportWorker = 'export-worker',
  Persistence = 'persistence',
  SourceLoad = 'source-load',
}

export enum ReportingPointerType {
  Coarse = 'coarse',
  Fine = 'fine',
}

export enum ReportingTag {
  AppVersion = 'diagnostics.appVersion',
  BuildMode = 'diagnostics.buildMode',
  CanvasFilter = 'diagnostics.canvasFilter',
  CanvasMaxArea = 'canvas.maxArea',
  DiagnosticsCanvasMaxArea = 'diagnostics.canvasMaxArea',
  ColorGamutP3 = 'diagnostics.colorGamutP3',
  ColorScheme = 'diagnostics.colorScheme',
  DeviceMemory = 'diagnostics.deviceMemory',
  DevicePixelRatio = 'diagnostics.devicePixelRatio',
  HardwareConcurrency = 'diagnostics.hardwareConcurrency',
  NativeAvif = 'diagnostics.nativeAvif',
  NativeHeic = 'diagnostics.nativeHeic',
  OffscreenCanvasWorker = 'diagnostics.offscreenCanvasWorker',
  RecentsCount = 'diagnostics.recentsCount',
  SentryEnabled = 'diagnostics.sentryEnabled',
  SentryLastEventId = 'diagnostics.sentryLastEventId',
  ServiceWorker = 'diagnostics.serviceWorker',
  ShareFiles = 'diagnostics.shareFiles',
  ShareTarget = 'diagnostics.shareTarget',
  StorageEstimate = 'diagnostics.storageEstimate',
  StoragePersisted = 'diagnostics.storagePersisted',
  Viewport = 'diagnostics.viewport',
}

export enum ReportingEvent {
  SourceLoaded = 'source-loaded',
  ToolChanged = 'tool-changed',
  ExportStarted = 'export-started',
  ExportFinished = 'export-finished',
  ExportFailed = 'export-failed',
  PreviewOpened = 'preview-opened',
  QualityCompareOpened = 'quality-compare-opened',
  RecentRestored = 'recent-restored',
}

export enum UsageEvent {
  SourceLoaded = 'source-loaded',
  ExportFinished = 'export-finished',
  ExportFailed = 'export-failed',
  ToolUsed = 'tool-used',
  PreviewOpened = 'preview-opened',
  QualityCompareOpened = 'quality-compare-opened',
  RecentRestored = 'recent-restored',
  ShareOpened = 'share-opened',
  SharedIn = 'shared-in',
  ShareCancelled = 'share-cancelled',
  SaveClicked = 'save-clicked',
  ContactClicked = 'contact-clicked',
  CollageLeft = 'collage-left',
}

export interface UsageDataByEvent {
  [UsageEvent.SourceLoaded]: { mimeType: string; megapixels: number }
  [UsageEvent.ExportFinished]: {
    rootKind: NodeKind
    presetId: OutputPresetId
    splitKind: SplitKind | null
    format: ExportFormat
    resizeMode: ResizeMode
    outputs: number
    totalBytes: number
  }
  [UsageEvent.ExportFailed]: { errorName: string }
  [UsageEvent.ToolUsed]: { toolId: ToolTab }
  [UsageEvent.PreviewOpened]: Record<never, never>
  [UsageEvent.QualityCompareOpened]: Record<never, never>
  [UsageEvent.RecentRestored]: Record<never, never>
  [UsageEvent.ShareOpened]: { files: number }
  [UsageEvent.SharedIn]: { files: number }
  [UsageEvent.ShareCancelled]: Record<never, never>
  [UsageEvent.SaveClicked]: { files: number }
  [UsageEvent.ContactClicked]: { channel: ContactChannel }
  [UsageEvent.CollageLeft]: Record<never, never>
}

export type UsageData = UsageDataByEvent[UsageEvent]

export interface ReportingDocumentShape {
  rootKind: NodeKind
  nodeCount: number
  presetId: OutputPresetId
  splitKind: SplitKind | null
  format: ExportFormat
  resizeMode: ResizeMode
  jpegQuality: number
}

export interface ReportingContext {
  stage?: ReportingStage
  toolId?: ToolTab | UtilityId
  pointerType?: ReportingPointerType
  documentShape?: ReportingDocumentShape
}

export interface ReportingConfiguration {
  dsn: string | undefined
  mode: string
}
